<template>
  <div
    class="filter-and-sort"
    :class="{ [$class('active')]: hasActiveFilters }"
  >
    <ul
      v-if="hasActiveFilters"
      class="filter-and-sort__active-filters"
    >
      <li
        v-for="option of criteria"
        :key="option.value"
        class="filter-and-sort__option-button-wrapper"
      >
        <btn
          class="main-collection__filter-button filter-and-sort__option-button"
          :label="option.name ? option.name : option.value"
          modifiers="secondary"
          type="button"
          show-icon
          @click="clearCriteria(option, true)"
          @keydown.enter="clearCriteria(option, true)"
        >
          <template #icon>
            <icon-close aria-hidden="true" />

            <span
              class="visually-hidden"
              v-text="$string('collection.filters.remove')"
            />
          </template>
        </btn>
      </li>

      <li>
        <btn
          class="main-collection__filter-button filter-and-sort__clear-all-button"
          :label="$string('collection.filters.clear')"
          modifiers="secondary"
          type="button"
          @click="clearAll"
          @keydown.enter="clearAll"
        />
      </li>
    </ul>

    <btn
      class="main-collection__filter-button"
      :label="$string('collection.filters.button_label')"
      modifiers="secondary"
      show-icon
      type="button"
      @click="handleOverlayToggle"
      @keydown.enter.prevent="handleOverlayToggle"
    >
      <template #icon>
        <icon-arrow />
      </template>
    </btn>
  </div>

  <overlay
    id="filter-and-sort-drawer"
    ref="overlay"
    key="filter-and-sort"
    block-class="filter-and-sort-overlay"
    direction="left"
    namespace="filter-and-sort"
    type="drawer"
  >
    <template #title>
      <span
        class="text-other-label-bold"
        v-text="$string('collection.filters.drawer_title')"
      />
    </template>

    <template #body>
      <fieldset class="filter-and-sort__sort-wrapper">
        <legend
          class="filter-and-sort__sort-title text-other-label-regular"
          v-text="$string('collection.filters.sort')"
        />

        <ul v-if="renderSortOptions">
          <li
            v-for="option of sortOptions.options"
            :key="option.value"
            class="filter-and-sort__option"
          >
            <radio-input
              :id="`sort_by.${option.value}`"
              :model-value="sortOptions.current"
              class="filter-and-sort__option-input"
              label-class="filter-and-sort__option-label text-body-m-regular"
              :checked="option.name === defaultSortName"
              :label="option.name"
              name="sort"
              type="radio"
              :value="option.value"
              @update:model-value="updateCriteria(option)"
            />
          </li>
        </ul>
      </fieldset>

      <fieldset
        v-for="(filter, index) of visibleFilters"
        :key="index"
        class="filter-and-sort__filter-wrapper"
      >
        <legend
          class="filter-and-sort__filter-title text-other-label-regular"
          v-text="filter.label"
        />

        <ul
          class="filter-and-sort__filter"
          :class="`filter-and-sort__filter-${filter.label.toLowerCase()}`"
        >
          <li
            v-for="option of getOptionsWithProducts(filter.options)"
            :key="option.value"
            class="filter-and-sort__option"
          >
            <checkbox-input
              :id="`${getFilterHandle(option.param)}.${option.handle}`"
              class="filter-and-sort__option-input"
              label-class="filter-and-sort__option-label text-body-m-regular"
              :label="option.value"
              :model-value="optionsExists(option)"
              :color-swatch="option.swatch"
              :name="`${getFilterHandle(option.param)}.${option.handle}`"
              :value="option.handle"
              @update:model-value="updateCriteria(option)"
            />
          </li>
        </ul>
      </fieldset>

      <div class="filter-and-sort__actions">
        <template v-if="criteria.length">
          <btn
            :label="$string('collection.filters.apply', {
              replace: { count: productCount },
              pluralise: productCount
            })"
            modifiers="primary center block"
            type="button"
            @click="handleApplyButtonClick"
            @keydown.enter="handleApplyButtonClick"
          />

          <btn
            :label="$string('collection.filters.clear')"
            modifiers="secondary center block"
            type="button"
            @click="clearAll"
            @keydown.enter="clearAll"
          />
        </template>

        <template v-else>
          <btn
            :label="$string('collection.filters.done')"
            modifiers="primary center block"
            type="button"
            @click="closeOverlay('filter-and-sort')"
            @keydown.enter="closeOverlay('filter-and-sort')"
          />
        </template>
      </div>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Main filters (filters)
 * -----------------------------------------------------------------------------
 * Filters used for collection and search pages.
 *
 * @param {Object} filters - Formatted Liquid filters object.
 * @param {Object} sortOptions - Formatted Liquid sort options object.
 * @param {String} currentSort - Current sort option.
 * @param {Number} productCount - Number of product filter results.
 *
 * @emits updateDrawerCriteria - Emit drawer criteria update.
 * @emits updateProducts - Emit product update.
 */
import { mapActions } from 'vuex'

import CheckboxInput from '~async/utils/checkbox-input/checkbox-input'
import RadioInput from '~async/utils/radio-input/radio-input'
import Btn from '~global/btn/btn.vue'
import Overlay from '~global/overlay/overlay'
import IconArrow from '~icons/directional-navigation/arrow/down-2.svg'
import IconClose from '~icons/directional-navigation/close.svg'

export default {
  name: 'FilterAndSort',

  components: {
    Btn,
    CheckboxInput,
    RadioInput,
    IconArrow,
    IconClose,
    Overlay,
  },

  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    filterVisibility: {
      type: Object,
      default: () => ({}),
    },
    sortOptions: {
      type: Object,
      default: () => ({}),
    },
    currentSort: {
      type: String,
      default: '',
    },
    productCount: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'update-drawer-criteria': (payload) => typeof payload === 'object',
    'update-products': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      criteria: [],
      filterAndSortDrawerActive: false,
      renderSortOptions: true,
    }
  },

  computed: {

    /**
     * Returns the default sort option name
     * @returns {String}
     */
    defaultSortName() {
      return this.defaultSort?.name
    },

    /**
     * Return the current option based on user selection or default value.
     * @returns {Object}
     */
    defaultSort() {
      return this.sortOptions.options.find(
        (option) => option.value === this.currentSort,
      )
    },

    /**
     * Compute if there are currently active filter options.
     * @returns {Boolean}
     */
    hasActiveFilters() {
      return this.criteria && this.criteria?.length
    },

    /**
   * Get the visible filters based on the filter visibility metafield settings.
   * @returns {Array}
   */
    visibleFilters() {
      if (!this.filterVisibility) {
        return this.filters
      }

      return this.filters?.filter(filter => {
        const visibility = this.filterVisibility[filter?.label]
        return visibility
      })
    },
  },

  mounted() {
    this.initCriteria()
    this.$refs.overlay.addCallback(this.resetCriteria)
  },

  methods: {

    /**
     * Map Vuex actions.
    */
    ...mapActions({
      closeOverlay: 'overlays/close',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Applies criteria to update product grid. If mobile, also closes drawer.
     */
    handleApplyButtonClick() {
      this.applyCriteria()

      this.closeOverlay('filter-and-sort')
    },

    /**
     * Handle menu toggle.
     */
    handleOverlayToggle() {
      this.filterAndSortDrawerActive = !this.filterAndSortDrawerActive

      this.toggleOverlay({
        component: 'filter-and-sort',
        ignoreDismissed: true,
        namespace: 'filter-and-sort',
      })
    },

    /**
     * Get filter handle.
     * @param {String} filterParam
     * @returns {String}
     */
    getFilterHandle(filterParam) {
      return filterParam.split('.').slice(-1)[0]
    },

    /**
     * Format display name for sort and size options.
     * @param {String} value
     * @param {String} key
     */
    getCriteriaName(value, key) {
      let name = value

      if (key === 'sort_by') {
        name = this.sortOptions.options.find(option => option.value === value).name
      }

      if (key.toLowerCase().includes('size')) {
        name = this.$string('collection.filters.size', {
          replace: { size: value },
        })
      }

      return name
    },

    /**
     * Get only filter options that have products in this collection.
     * @param {Array} options - All options for this filter
     * @returns {Array}
     */
    getOptionsWithProducts(options) {
      return options.filter(option => option.count > 0)
    },

    /**
     * Resets criteria by removing any checked, but not applied,
     * filter options on drawer close.
     */
    resetCriteria() {
      this.filterAndSortDrawerActive = false

      const params = new URLSearchParams(window.location.search)
      const paramCriteriaArray = []

      params.forEach((value, key) => {
        if (!key.includes('filter') && !key.includes('sort_by')) {
          return
        }

        const name = this.getCriteriaName(value, key)

        const paramCriteria = {
          name,
          param: key,
          value,
        }

        paramCriteriaArray.push(paramCriteria)

        if (this.optionsExists(paramCriteria)) {
          return
        }

        this.criteria.push(paramCriteria)
      })

      if (this.criteria.length === paramCriteriaArray.length) {
        this.$emit('update-drawer-criteria', this.criteria, false, true)
        return
      }

      this.criteria = this.criteria.filter(option => {
        const index = paramCriteriaArray.findIndex((paramCriteria) => {
          return (
            paramCriteria.param === option.param &&
            paramCriteria.value === option.value
          )
        })

        return index >= 0
      })

      this.resetDefaultSort()
      this.$emit('update-drawer-criteria', this.criteria, false)
    },

    /**
     * Populate criteria with filter data from URL parameters.
     */
    initCriteria() {
      const params = new URLSearchParams(window.location.search)

      params.forEach((value, key) => {
        if (!key.includes('filter') && key !== 'sort_by') {
          return
        }

        const name = this.getCriteriaName(value, key)

        this.criteria.push({
          name,
          param: key,
          value,
        })
      })
    },

    /**
     * Apply filter criteria.
     * @param {Boolean} clearAll - Whether filters are all being cleared
     */
    applyCriteria(clearAll) {
      this.$emit('update-products', this.criteria, true, clearAll)
    },

    /**
     * Get option name to render as an active filter button.
     * @param {Object} option - Chosen option
     */
    getOptionName(option) {
      if (option.name) {
        return option.name
      }

      if (option.param.toLowerCase().includes('size')) {
        return this.$string('collection.filters.size', {
          replace: { size: option.value },
        })
      }

      return option.value
    },

    /**
     * Add/Remove filter criteria in drawer.
     * @param {Object} option - Chosen option
     */
    updateCriteria(option) {
      const optionObject = {
        name: this.getOptionName(option),
        param: option.param,
        value: option.value,
      }

      if (this.optionsExists(option)) {
        this.clearCriteria(option, false)
      } else if (option.param === 'sort_by') {
        const sortOptionIndex = this.criteria.findIndex(criteria => criteria.param === 'sort_by')

        if (sortOptionIndex >= 0) {
          this.criteria.splice(sortOptionIndex, 1, optionObject)
        } else {
          this.criteria.unshift(optionObject)
        }

      } else {
        this.criteria.push(optionObject)
      }

      this.$emit('update-drawer-criteria', this.criteria, false)
    },

    /**
     * Clear all filters.
     */
    clearAll() {
      this.criteria = []
      this.resetDefaultSort()
      this.applyCriteria(true)
    },

    /**
     * Clear specific criteria from button click.
     * @param {Object} option - Chosen option
     * @param {Boolean} applyCriteria - Whether to apply criteria
     */
    clearCriteria(option, applyCriteria) {
      const index = this.criteria.findIndex((criteria) => {
        return (
          criteria.param === option.param &&
          criteria.value === option.value
        )
      })

      if (index < 0) {
        return
      }

      if (option.param === 'sort_by') {
        this.resetDefaultSort()
      }

      this.criteria.splice(index, 1)

      if (applyCriteria) {
        const clearAll = this.criteria.length > 0

        this.applyCriteria(clearAll)
      }
    },

    /**
     * Reset sort radio to default by re-rendering options list.
     */
    resetDefaultSort() {
      this.renderSortOptions = false
      this.$nextTick().then(() => {
        this.renderSortOptions = true
      })
    },

    /**
     * Check if filter option exists in criteria.
     * @param {Object} option
     * @returns {Boolean}
     */
    optionsExists(option) {
      return this.criteria.some((criteria) => {
        return (
          criteria.param === option.param &&
          criteria.value === option.value
        )
      })
    },
  },

}
</script>

<style lang="scss">
@import './filter-and-sort';
</style>
